import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ShoppingCart, TrendingUp, DollarSign, Search, Bot, Mail,
  Share2, Users, Globe, ExternalLink, PieChart, BarChart3,
  Info, ArrowUpRight, ArrowDownRight, Minus, Calendar,
  Target, Repeat, UserPlus, Crown, Sparkles, MousePointer
} from 'lucide-react'
import {
  PieChart as RechartsPie, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, AreaChart, Area
} from 'recharts'
import { seoAnalytics } from '@/lib/api'

interface ConversionsDashboardProps {
  days: number
  section?: string
}

type TabType = 'overview' | 'categories' | 'sources' | 'customers' | 'trends'

// Tooltip component for explanations
function InfoTooltip({ text }: { text: string }) {
  return (
    <div className="group relative inline-flex items-center ml-1">
      <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 max-w-xs">
        <div className="whitespace-normal">{text}</div>
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-700" />
      </div>
    </div>
  )
}

// Category definitions with descriptions
const CATEGORY_INFO: Record<string, {
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
  bgColor: string
  description: string
}> = {
  paid: {
    label: 'Publicidad Pagada',
    icon: DollarSign,
    color: '#f59e0b',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    description: 'Pedidos de campañas de pago: Google Ads, Facebook Ads, Bing Ads. Usuarios que hicieron clic en un anuncio.'
  },
  organic: {
    label: 'SEO Organico',
    icon: Search,
    color: '#22c55e',
    bgColor: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    description: 'Pedidos de busquedas organicas en Google, Bing, DuckDuckGo, Brave, Yahoo. Sin coste publicitario.'
  },
  ai: {
    label: 'Asistentes IA',
    icon: Bot,
    color: '#a855f7',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    description: 'Pedidos originados desde ChatGPT, Perplexity, Copilot o Gemini. Nueva fuente de trafico emergente.'
  },
  email: {
    label: 'Email Marketing',
    icon: Mail,
    color: '#3b82f6',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    description: 'Pedidos desde campañas de email (Brevo/Sendinblue). Incluye newsletters y emails transaccionales.'
  },
  social: {
    label: 'Redes Sociales',
    icon: Share2,
    color: '#ec4899',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400',
    description: 'Pedidos desde Facebook, Instagram, Twitter/X, LinkedIn o YouTube.'
  },
  customer: {
    label: 'Clientes Recurrentes',
    icon: Users,
    color: '#8b5cf6',
    bgColor: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400',
    description: 'Clientes que ya conocen la tienda (>90 dias de antiguedad). Entran directamente sin campañas.'
  },
  direct: {
    label: 'Trafico Directo',
    icon: Globe,
    color: '#64748b',
    bgColor: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-400',
    description: 'Clientes nuevos (<90 dias) que llegan sin fuente identificable. Puede incluir trafico no rastreado.'
  },
  referral: {
    label: 'Referencias',
    icon: ExternalLink,
    color: '#f43f5e',
    bgColor: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
    description: 'Pedidos desde enlaces en otras webs, foros, blogs o directorios.'
  },
}

// Source definitions with descriptions
const SOURCE_INFO: Record<string, { description: string }> = {
  google_ads: { description: 'Campañas de Google Ads (Search, Shopping, Display). Detectado por parametro gclid.' },
  google_organic: { description: 'Busquedas organicas en Google. El usuario encontro la tienda buscando en Google.' },
  bing_organic: { description: 'Busquedas en Bing. Incluye usuarios de Microsoft Edge con Bing por defecto.' },
  brave_organic: { description: 'Busquedas en Brave Search. Navegador centrado en privacidad.' },
  duckduckgo_organic: { description: 'Busquedas en DuckDuckGo. Buscador que no rastrea usuarios.' },
  ai_chatgpt: { description: 'Referencias desde ChatGPT de OpenAI. El usuario pregunto al asistente y este recomendo la tienda.' },
  ai_perplexity: { description: 'Referencias desde Perplexity AI. Buscador con IA que cita fuentes.' },
  ai_copilot: { description: 'Referencias desde Microsoft Copilot (antes Bing Chat).' },
  email_brevo: { description: 'Clics desde emails enviados con Brevo (Sendinblue). Newsletters o emails automaticos.' },
  customer_loyal: { description: 'Clientes fidelizados con mas de 1 año de antiguedad. Ya conocen la tienda.' },
  customer_returning: { description: 'Clientes recurrentes (90 dias - 1 año). Han comprado antes recientemente.' },
  direct_new: { description: 'Clientes nuevos (<90 dias) sin fuente de trafico identificada.' },
  referral_other: { description: 'Trafico desde otros sitios web que enlazan a la tienda.' },
}

const TABS: { id: TabType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'overview', label: 'Resumen', icon: PieChart },
  { id: 'categories', label: 'Categorias', icon: BarChart3 },
  { id: 'sources', label: 'Fuentes', icon: Target },
  { id: 'customers', label: 'Clientes', icon: Users },
  { id: 'trends', label: 'Tendencias', icon: TrendingUp },
]

export default function ConversionsDashboard({ days, section }: ConversionsDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview')

  const { data, isLoading, error } = useQuery({
    queryKey: ['seo-attribution-summary', days, section],
    queryFn: () => seoAnalytics.getAttributionSummary(days, section),
  })

  const { data: trendsData } = useQuery({
    queryKey: ['seo-conversions-by-date', days, section],
    queryFn: () => seoAnalytics.getConversionsByDate({ days, section }),
    enabled: activeTab === 'trends',
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
          {TABS.map((tab) => (
            <div key={tab.id} className="px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse w-24 h-8" />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-2" />
              <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-6 text-center text-red-500">
        Error al cargar datos de atribucion. Verifica que el servidor este ejecutandose.
      </div>
    )
  }

  const summary = data?.data
  const totals = summary?.totals || { orders: 0, revenue: 0, avg_order: 0 }
  const byCategory = summary?.by_category || []
  const bySource = summary?.by_source || []

  // Calculate key metrics
  const marketingCategories = ['paid', 'organic', 'ai', 'email', 'social', 'referral']
  const marketingOrders = byCategory
    .filter((c) => marketingCategories.includes(c.category))
    .reduce((sum, c) => sum + c.orders, 0)
  const customerOrders = byCategory
    .filter((c) => c.category === 'customer')
    .reduce((sum, c) => sum + c.orders, 0)
  const directOrders = byCategory
    .filter((c) => c.category === 'direct')
    .reduce((sum, c) => sum + c.orders, 0)

  const marketingRevenue = byCategory
    .filter((c) => marketingCategories.includes(c.category))
    .reduce((sum, c) => sum + c.revenue, 0)
  const customerRevenue = byCategory
    .filter((c) => c.category === 'customer')
    .reduce((sum, c) => sum + c.revenue, 0)

  return (
    <div className="space-y-4">
      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 pb-px overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap
                ${isActive
                  ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 border border-b-0 border-slate-200 dark:border-slate-700 -mb-px'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
        {activeTab === 'overview' && (
          <OverviewTab
            totals={totals}
            byCategory={byCategory}
            bySource={bySource}
            marketingOrders={marketingOrders}
            customerOrders={customerOrders}
            directOrders={directOrders}
            marketingRevenue={marketingRevenue}
            customerRevenue={customerRevenue}
            days={days}
          />
        )}

        {activeTab === 'categories' && (
          <CategoriesTab byCategory={byCategory} totals={totals} />
        )}

        {activeTab === 'sources' && (
          <SourcesTab bySource={bySource} totals={totals} />
        )}

        {activeTab === 'customers' && (
          <CustomersTab
            bySource={bySource}
            byCategory={byCategory}
            totals={totals}
            customerOrders={customerOrders}
            customerRevenue={customerRevenue}
          />
        )}

        {activeTab === 'trends' && (
          <TrendsTab data={trendsData?.data || []} days={days} />
        )}
      </div>
    </div>
  )
}

// Overview Tab Component
function OverviewTab({
  totals,
  byCategory,
  bySource,
  marketingOrders,
  customerOrders,
  directOrders,
  marketingRevenue,
  customerRevenue,
  days,
}: {
  totals: { orders: number; revenue: number; avg_order: number }
  byCategory: any[]
  bySource: any[]
  marketingOrders: number
  customerOrders: number
  directOrders: number
  marketingRevenue: number
  customerRevenue: number
  days: number
}) {
  const marketingPct = totals.orders > 0 ? (marketingOrders / totals.orders) * 100 : 0
  const customerPct = totals.orders > 0 ? (customerOrders / totals.orders) * 100 : 0
  const directPct = totals.orders > 0 ? (directOrders / totals.orders) * 100 : 0

  // Pie chart data
  const pieData = byCategory.map((cat) => ({
    name: cat.label,
    value: cat.orders,
    color: CATEGORY_INFO[cat.category]?.color || '#64748b',
  }))

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">Pedidos Totales</span>
            <ShoppingCart className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white">{totals.orders.toLocaleString()}</div>
          <div className="text-xs text-slate-500 mt-1">Ultimos {days} dias</div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-green-600 dark:text-green-400 font-medium">Ingresos</span>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white">
            €{totals.revenue.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
          <div className="text-xs text-slate-500 mt-1">Ticket medio: €{totals.avg_order.toFixed(2)}</div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">Adquisicion</span>
              <InfoTooltip text="Pedidos conseguidos a traves de campañas de marketing: SEO, Ads, Email, Redes Sociales, IA" />
            </div>
            <TrendingUp className="w-5 h-5 text-amber-500" />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white">{marketingPct.toFixed(1)}%</div>
          <div className="text-xs text-slate-500 mt-1">{marketingOrders} pedidos · €{marketingRevenue.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
        </div>

        <div className="bg-gradient-to-br from-violet-50 to-violet-100 dark:from-violet-900/20 dark:to-violet-800/20 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <span className="text-sm text-violet-600 dark:text-violet-400 font-medium">Retencion</span>
              <InfoTooltip text="Pedidos de clientes que ya conocian la tienda (>90 dias de antiguedad). No necesitan campañas." />
            </div>
            <Users className="w-5 h-5 text-violet-500" />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white">{customerPct.toFixed(1)}%</div>
          <div className="text-xs text-slate-500 mt-1">{customerOrders} pedidos · €{customerRevenue.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
        </div>
      </div>

      {/* Chart + Top Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
            Distribucion por Categoria
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value: number) => [`${value} pedidos`, '']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Sources */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
            Top Fuentes de Trafico
          </h3>
          <div className="space-y-3">
            {bySource.slice(0, 6).map((source, index) => {
              const pct = totals.orders > 0 ? (source.orders / totals.orders) * 100 : 0
              return (
                <div key={source.source} className="flex items-center gap-3">
                  <div className="w-6 text-center text-sm font-medium text-slate-400">
                    {index + 1}
                  </div>
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: source.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                        {source.label}
                      </span>
                      {SOURCE_INFO[source.source] && (
                        <InfoTooltip text={SOURCE_INFO[source.source].description} />
                      )}
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-1.5 mt-1">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: source.color }}
                      />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">
                      {source.orders}
                    </div>
                    <div className="text-xs text-slate-500">{pct.toFixed(1)}%</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// Categories Tab Component
function CategoriesTab({
  byCategory,
  totals,
}: {
  byCategory: any[]
  totals: { orders: number; revenue: number; avg_order: number }
}) {
  // Bar chart data
  const barData = byCategory.map((cat) => ({
    name: cat.label.split(' ')[0], // Shortened label
    fullName: cat.label,
    orders: cat.orders,
    revenue: cat.revenue,
    color: CATEGORY_INFO[cat.category]?.color || '#64748b',
  }))

  return (
    <div className="space-y-6">
      {/* Bar Chart */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
          Pedidos por Categoria
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
              <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis
                type="category"
                dataKey="fullName"
                tick={{ fontSize: 12, fill: '#64748b' }}
                width={100}
              />
              <RechartsTooltip
                formatter={(value: number, name: string) => [
                  name === 'orders' ? `${value} pedidos` : `€${value.toLocaleString()}`,
                  name === 'orders' ? 'Pedidos' : 'Ingresos'
                ]}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="orders" radius={[0, 4, 4, 0]}>
                {barData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {byCategory.map((cat) => {
          const info = CATEGORY_INFO[cat.category]
          if (!info) return null
          const Icon = info.icon
          const pct = totals.orders > 0 ? (cat.orders / totals.orders) * 100 : 0
          const avgTicket = cat.orders > 0 ? cat.revenue / cat.orders : 0

          return (
            <div
              key={cat.category}
              className={`rounded-xl p-4 ${info.bgColor}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold">{info.label}</span>
                </div>
                <InfoTooltip text={info.description} />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-2xl font-bold">{cat.orders}</span>
                  <span className="text-sm opacity-75">{pct.toFixed(1)}%</span>
                </div>

                <div className="text-sm opacity-75">
                  €{cat.revenue.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} · Ticket €{avgTicket.toFixed(2)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Sources Tab Component
function SourcesTab({
  bySource,
  totals,
}: {
  bySource: any[]
  totals: { orders: number; revenue: number; avg_order: number }
}) {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
        Detalle por Fuente de Trafico
        <InfoTooltip text="Cada fuente indica de donde vino el cliente antes de realizar el pedido. Se usa la ultima fuente conocida (last-touch attribution)." />
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-slate-200 dark:border-slate-700">
              <th className="pb-3 font-semibold text-slate-600 dark:text-slate-400">Fuente</th>
              <th className="pb-3 font-semibold text-slate-600 dark:text-slate-400">Categoria</th>
              <th className="pb-3 font-semibold text-slate-600 dark:text-slate-400 text-right">Pedidos</th>
              <th className="pb-3 font-semibold text-slate-600 dark:text-slate-400 text-right">%</th>
              <th className="pb-3 font-semibold text-slate-600 dark:text-slate-400 text-right">Ingresos</th>
              <th className="pb-3 font-semibold text-slate-600 dark:text-slate-400 text-right">Ticket</th>
              <th className="pb-3 font-semibold text-slate-600 dark:text-slate-400 text-right">
                Confianza
                <InfoTooltip text="Nivel de certeza en la atribucion. 90%+ = muy fiable, 50-90% = probable, <50% = estimado" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {bySource.map((source) => {
              const pct = totals.orders > 0 ? (source.orders / totals.orders) * 100 : 0
              const catInfo = CATEGORY_INFO[source.category]
              const sourceInfo = SOURCE_INFO[source.source]
              const confidence = source.confidence * 100

              return (
                <tr key={source.source} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: source.color }}
                      />
                      <span className="font-medium text-slate-800 dark:text-slate-200">
                        {source.label}
                      </span>
                      {sourceInfo && (
                        <InfoTooltip text={sourceInfo.description} />
                      )}
                    </div>
                  </td>
                  <td className="py-3">
                    {catInfo && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${catInfo.bgColor}`}>
                        {catInfo.label}
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-right font-semibold text-slate-900 dark:text-white">
                    {source.orders.toLocaleString()}
                  </td>
                  <td className="py-3 text-right text-slate-600 dark:text-slate-400">
                    {pct.toFixed(1)}%
                  </td>
                  <td className="py-3 text-right text-slate-600 dark:text-slate-400">
                    €{source.revenue.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </td>
                  <td className="py-3 text-right text-slate-600 dark:text-slate-400">
                    €{source.avg_order.toFixed(2)}
                  </td>
                  <td className="py-3 text-right">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                      ${confidence >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : confidence >= 50 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}
                    >
                      {confidence.toFixed(0)}%
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Customers Tab Component
function CustomersTab({
  bySource,
  byCategory,
  totals,
  customerOrders,
  customerRevenue,
}: {
  bySource: any[]
  byCategory: any[]
  totals: { orders: number; revenue: number; avg_order: number }
  customerOrders: number
  customerRevenue: number
}) {
  // Find loyal and returning
  const loyalSource = bySource.find((s) => s.source === 'customer_loyal')
  const returningSource = bySource.find((s) => s.source === 'customer_returning')
  const newSource = bySource.find((s) => s.source === 'direct_new')

  const loyalOrders = loyalSource?.orders || 0
  const loyalRevenue = loyalSource?.revenue || 0
  const returningOrders = returningSource?.orders || 0
  const returningRevenue = returningSource?.revenue || 0
  const newOrders = newSource?.orders || 0
  const newRevenue = newSource?.revenue || 0

  const retentionRate = totals.orders > 0 ? (customerOrders / totals.orders) * 100 : 0
  const loyalPct = totals.orders > 0 ? (loyalOrders / totals.orders) * 100 : 0
  const avgTicketLoyal = loyalOrders > 0 ? loyalRevenue / loyalOrders : 0
  const avgTicketNew = newOrders > 0 ? newRevenue / newOrders : 0

  return (
    <div className="space-y-6">
      {/* Customer Segments */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Loyal Customers */}
        <div className="bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900/20 dark:to-purple-900/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
              <Crown className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h4 className="font-semibold text-violet-900 dark:text-violet-100">Clientes Fidelizados</h4>
              <p className="text-xs text-violet-600 dark:text-violet-400">Mas de 1 año de antiguedad</p>
            </div>
            <InfoTooltip text="Clientes que llevan mas de 1 año registrados. Son los mas valiosos porque compran sin necesidad de campañas." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-3xl font-bold text-violet-900 dark:text-violet-100">{loyalOrders}</div>
              <div className="text-sm text-violet-600 dark:text-violet-400">pedidos</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-violet-900 dark:text-violet-100">
                €{loyalRevenue.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
              </div>
              <div className="text-sm text-violet-600 dark:text-violet-400">ingresos</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-violet-200 dark:border-violet-800">
            <div className="flex justify-between text-sm">
              <span className="text-violet-600 dark:text-violet-400">% del total</span>
              <span className="font-semibold text-violet-900 dark:text-violet-100">{loyalPct.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Returning Customers */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-100 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Repeat className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-100">Clientes Recurrentes</h4>
              <p className="text-xs text-blue-600 dark:text-blue-400">90 dias - 1 año</p>
            </div>
            <InfoTooltip text="Clientes relativamente nuevos (90 dias a 1 año) que ya han hecho compras anteriores." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">{returningOrders}</div>
              <div className="text-sm text-blue-600 dark:text-blue-400">pedidos</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                €{returningRevenue.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-400">ingresos</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
            <div className="flex justify-between text-sm">
              <span className="text-blue-600 dark:text-blue-400">% del total</span>
              <span className="font-semibold text-blue-900 dark:text-blue-100">
                {totals.orders > 0 ? ((returningOrders / totals.orders) * 100).toFixed(1) : 0}%
              </span>
            </div>
          </div>
        </div>

        {/* New Customers */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h4 className="font-semibold text-emerald-900 dark:text-emerald-100">Clientes Nuevos</h4>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">Menos de 90 dias</p>
            </div>
            <InfoTooltip text="Clientes nuevos (<90 dias) que llegaron sin fuente de trafico identificada. Pueden ser recomendaciones boca a boca." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-3xl font-bold text-emerald-900 dark:text-emerald-100">{newOrders}</div>
              <div className="text-sm text-emerald-600 dark:text-emerald-400">pedidos</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                €{newRevenue.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
              </div>
              <div className="text-sm text-emerald-600 dark:text-emerald-400">ingresos</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-800">
            <div className="flex justify-between text-sm">
              <span className="text-emerald-600 dark:text-emerald-400">% del total</span>
              <span className="font-semibold text-emerald-900 dark:text-emerald-100">
                {totals.orders > 0 ? ((newOrders / totals.orders) * 100).toFixed(1) : 0}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Retention Insights */}
      <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-5">
        <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          Insights de Retencion
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
            <div className="text-sm text-slate-500 dark:text-slate-400 mb-1 flex items-center">
              Tasa de Retencion
              <InfoTooltip text="Porcentaje de pedidos de clientes que ya conocian la tienda (>90 dias)." />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">{retentionRate.toFixed(1)}%</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
            <div className="text-sm text-slate-500 dark:text-slate-400 mb-1 flex items-center">
              Ticket Medio Fidelizado
              <InfoTooltip text="Valor medio de pedido de clientes fidelizados (>1 año)." />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">€{avgTicketLoyal.toFixed(2)}</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
            <div className="text-sm text-slate-500 dark:text-slate-400 mb-1 flex items-center">
              Ticket Medio Nuevo
              <InfoTooltip text="Valor medio de pedido de clientes nuevos (<90 dias)." />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">€{avgTicketNew.toFixed(2)}</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
            <div className="text-sm text-slate-500 dark:text-slate-400 mb-1 flex items-center">
              Diferencia Ticket
              <InfoTooltip text="Diferencia entre ticket de cliente fidelizado vs nuevo. Positivo = fidelizados gastan mas." />
            </div>
            <div className={`text-2xl font-bold flex items-center gap-1 ${avgTicketLoyal > avgTicketNew ? 'text-green-600' : avgTicketLoyal < avgTicketNew ? 'text-red-600' : 'text-slate-600'}`}>
              {avgTicketLoyal > avgTicketNew ? <ArrowUpRight className="w-5 h-5" /> : avgTicketLoyal < avgTicketNew ? <ArrowDownRight className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
              €{Math.abs(avgTicketLoyal - avgTicketNew).toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Trends Tab Component
function TrendsTab({ data, days }: { data: any[]; days: number }) {
  // Process data for chart
  const processedData = data.reduce((acc: any[], item: any) => {
    const existing = acc.find((d) => d.date === item.date)
    if (existing) {
      existing[item.source] = (existing[item.source] || 0) + item.conversions
      existing.total = (existing.total || 0) + item.conversions
    } else {
      acc.push({
        date: item.date,
        dateFormatted: new Date(item.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
        [item.source]: item.conversions,
        total: item.conversions,
      })
    }
    return acc
  }, []).sort((a, b) => a.date.localeCompare(b.date))

  if (processedData.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No hay datos de tendencias disponibles</p>
        <p className="text-sm mt-1">Ejecuta una sincronizacion para obtener datos</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
        Evolucion de Conversiones (Ultimos {days} dias)
      </h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={processedData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="dateFormatted"
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
            />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
              }}
              formatter={(value: number) => [`${value} conversiones`, 'Total']}
            />
            <Area
              type="monotone"
              dataKey="total"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#colorTotal)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
